<template>
  <div class="address-exchange-read-modal">
    <div class="modal-frame" v-show="modalDisplay">
      <div class="backdrop" @click="closeModal"></div>
      <div class="modal">
        <div class="modal-header">
          <h1>連絡先交換QR読取</h1>
          <img src="/images/close_icon.svg" @click="closeModal" />
        </div>
        <div class="modal-body">
          <video ref="videoElement" v-show="modalDisplay" autoplay playsinline webkit-playsinline="true" muted></video>
          <canvas ref="canvasElement" style="display: none;"></canvas>
          <p v-if="scanErrorMessage" class="scan-error-message">{{ scanErrorMessage }}</p>
          <button v-if="scanErrorMessage" class="scan-retry-button" @click="retryScanning">再試行</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BrowserQRCodeReader } from '@zxing/library'
import { mapActions } from 'vuex'

// import ModalFrame from '@/components/common/ModalFrame.vue'

export default {
  name: 'AddressExchangeReadModal',


  components: {
    // ModalFrame,
  },

  props: {
    addressCategory: Object,
    modalDisplay: Boolean,
  },

  emits: [
    'addressExchangeFinishedEvent',
    'addressExchangeFailedEvent',
    'closeModalEvent',
  ],

  data () {
    return {
      codeReader: null,
      scanning: false,
      scanErrorNotified: false,
      scanErrorMessage: '',
      videoEventAttached: false,
    }
  },

  watch: {
    modalDisplay: {
      handler(newVal) {
        if (newVal) {
          this.startScanning()
        } else {
          this.stopScanning()
        }
      },
      immediate: true
    }
  },

  mounted() {
    this.codeReader = new BrowserQRCodeReader()
  },

  beforeUnmount() {
    this.stopScanning()
    this.removeVideoEventHandlers()
  },

  methods: {
    ...mapActions({
      exchangeAddress: 'addressExchangeToken/exchange',
      fetchAddressBook: 'addressBook/fetch',
    }),

    async startScanning() {
      if (!this.modalDisplay || this.scanning) return

      this.scanErrorMessage = ''

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.setScanErrorMessage('この環境ではカメラ機能を利用できません。ブラウザまたはアプリの設定をご確認ください。')
        return
      }
      
      try {
        this.scanning = true
        this.scanErrorNotified = false

        await this.$nextTick()
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          await new Promise(resolve => window.requestAnimationFrame(() => resolve()))
        }

        if (!this.modalDisplay) {
          this.scanning = false
          return
        }

        const videoElement = this.$refs.videoElement
        if (!videoElement) {
          this.setScanErrorMessage('QRリーダーの起動準備に失敗しました。画面を再読み込みして再度お試しください。')
          this.scanning = false
          return
        }
        this.addVideoEventHandlers(videoElement)
        videoElement.muted = true
        videoElement.setAttribute('playsinline', 'true')
        videoElement.setAttribute('webkit-playsinline', 'true')
        videoElement.playsInline = true

        let videoInputDevices = []
        try {
          videoInputDevices = await this.codeReader.listVideoInputDevices()
        } catch (deviceError) {
          console.warn('カメラデバイス一覧の取得に失敗しました。デフォルトカメラで起動を試みます。', deviceError)
        }
        
        // 背面カメラを優先的に選択
        const backCamera = videoInputDevices.find(device => 
          device.label.toLowerCase().includes('back') || 
          device.label.toLowerCase().includes('rear') ||
          device.label.toLowerCase().includes('environment')
        )
        
        const selectedDeviceId = backCamera ? backCamera.deviceId : videoInputDevices[0]?.deviceId

        // QRコードをスキャン
        this.codeReader.decodeFromVideoDevice(
          selectedDeviceId || undefined,
          videoElement,
          (result, error) => {
            if (result) {
              this.decoded(result.getText())
              this.stopScanning()
            }
            if (error && error.name !== 'NotFoundException') {
              console.error(error)
              if (!this.scanErrorNotified) {
                this.scanErrorNotified = true
                this.setScanErrorMessage(this.getScanErrorMessage(error))
              }
            }
          }
        )
      } catch (error) {
        console.error('カメラの起動に失敗しました:', error)
        this.scanning = false
        this.setScanErrorMessage(this.getScanErrorMessage(error))
      }
    },

    stopScanning() {
      if (this.codeReader) {
        this.codeReader.reset()
      }
      this.scanning = false
      this.scanErrorNotified = false
    },

    retryScanning() {
      this.stopScanning()
      this.startScanning()
    },

    addVideoEventHandlers(videoElement) {
      if (!videoElement || this.videoEventAttached) return
      videoElement.addEventListener('webkitendfullscreen', this.handleVideoFullscreenEnd)
      this.videoEventAttached = true
    },

    removeVideoEventHandlers() {
      const videoElement = this.$refs.videoElement
      if (!videoElement || !this.videoEventAttached) return
      videoElement.removeEventListener('webkitendfullscreen', this.handleVideoFullscreenEnd)
      this.videoEventAttached = false
    },

    handleVideoFullscreenEnd() {
      if (!this.modalDisplay) return
      this.scanning = false
      this.setScanErrorMessage('カメラ表示が中断されました。再試行してください。')
    },

    setScanErrorMessage(message) {
      this.scanErrorMessage = message
    },

    getScanErrorMessage(error) {
      if (error && error.name === 'AbortError') {
        return 'カメラ表示が中断されました。再試行してください。'
      }
      if (error && (error.name === 'NotAllowedError' || error.name === 'SecurityError')) {
        return 'カメラの利用が許可されていません。端末またはアプリの設定からカメラ権限を許可してください。'
      }
      if (error && error.name === 'NotFoundError') {
        return '利用可能なカメラが見つかりませんでした。端末にカメラがあるか確認してください。'
      }
      if (error && error.name === 'NotReadableError') {
        return 'カメラを利用できませんでした。他のアプリでカメラを使用中の場合は終了してから再度お試しください。'
      }
      return 'カメラの起動に失敗しました。画面を再読み込みして再度お試しください。'
    },

    decoded (decodedString) {
      console.log('QR DECODE COMPLETE:: ' + decodedString)
      this.scanErrorMessage = ''
      
      if (decodedString && decodedString.match(/([0-9a-f]{8})-([0-9a-f]{4})-([0-9a-f]{4})-([0-9a-f]{4})-([0-9a-f]{12})/) ) {
        this.exchangeAddress({ addressCategoryID: this.addressCategory.id, token: decodedString }).then(res => {
          console.log(res)
          if (res.success) {
            // result text create
            let resultMessage = '連絡先のQR読取りを完了しました。\n'
            if (res.own.exist) {
              resultMessage += '交換先にはすでにあなたの連絡先は登録済みでした。\n'
            }
            if (res.own.success) {
              resultMessage += '交換先にあなたの連絡先を登録しました。\n'
            }
            if (res.target.exist) {
              resultMessage += 'あなたの連絡帳にはすでに交換先は登録済みでした。\n'
            }
            if (res.target.success) {
              resultMessage += 'あなたの連絡帳に交換先を登録しました。\n'
            }
            // reload address
            this.fetchAddressBook()
            // emit
            this.$emit('addressExchangeFinishedEvent', resultMessage)
          }
        }).catch(error => {
          console.error(error)
          console.log(error.response.data)
          let errorMessage = '何らかの原因で連絡先交換できませんでした。システム管理者にお問い合わせください。'
          if (error.response.data && error.response.data.code) {
            if (error.response.data.code == 'SAMEUSER') {
              errorMessage = '自分のアドレス帳に自分自身を登録できません。'
            } else if (error.response.data.code == 'USED') {
              errorMessage = 'QRが利用済みです。一度QRを閉じて、再度QRを表示してから試してください。'
            } else if (error.response.data.code == 'SAVEERROR') {
              errorMessage = '連絡先の保存に失敗しました。サーバーの応答:' + error.response.data.message
            }
          }
          this.setScanErrorMessage(errorMessage)
        })
      } else {
        this.setScanErrorMessage('QRコードを認識できませんでした。画面内にQRコード全体が入るように再度お試しください。')
      }
    },

    closeModal () {
      this.stopScanning()
      this.scanErrorMessage = ''
      this.$emit('closeModalEvent')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/global.scss';
@import '@/assets/css/mediaQuery.scss';
@import '@/assets/css/colorTable.scss';

.address-exchange-read-modal {
  margin: 0;
  .modal-frame {
    .backdrop {
      position: absolute;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      height: 100dvh;
      background-color: #000;
      opacity: 0.3;
    }
    @supports(-webkit-touch-callout: none) {
      /* iPhoneの表示のみ指定を上書き */
      .backdrop {
        height: -webkit-fill-available;
      }
    }
    .modal {
      position: absolute;
      width: 800px;
      max-height: calc(100% - 60px);
      margin: 0;
      padding: 0;
      top: 30px;
      left: calc((100vw - 800px) / 2);
      overflow: auto;
      background-color: #fff;
      border: 1px solid #aaa;
      border-radius: 5px;
      @include mediaQuery('tablet') {
        width: calc(100vw - 40px);
        left: 20px;
      }
      @include mediaQuery('phone') {
        width: calc(100vw - 10px);
        max-height: calc(100% - 60px);
        top: 5px;
        left: 5px;
      }
      &.fullscreen {
        width: 100vw;
        height: 100vh;
        height: 100dvh;
        max-height: 100vh;
        max-height: 100dvh;
        top: 0;
        left: 0;
        border: none;
      }
      &.top {
        width: calc(100vw - 10px);
        max-height: calc(100vh - 60px);
        max-height: calc(100dvh - 60px);
        top: 5px;
        left: 5px;
      }
      &.heigh {
        width: calc(100vw - 10px);
        max-height: calc(100vh - 160px);
        max-height: calc(100dvh - 160px);
        top: 100px;
        left: 5px;
      }
      &.bottom {
        width: calc(100vw - 10px);
        max-height: calc(100vh - 60px);
        max-height: calc(100dvh - 60px);
        top: auto;
        bottom: 5px;
        left: 5px;
      }
      &.low {
        width: calc(100vw - 10px);
        max-height: calc(100vh - 160px);
        max-height: calc(100dvh - 160px);
        top: auto;
        bottom: 100px;
        left: 5px;
      }
      &.editing {
        background-color: #ddd;
      }
      .modal-header {
        padding: 10px;
        border-bottom: 1px solid #aaa;
        h1 {
          margin: 0;
          font-size: 14px;
          color: #666;
          text-align: center;
          img {
            margin-right: 10px;
            height: 15px;
          }
        }
      }
    }
  }
  .modal-header {
    display: flex;
    justify-content: space-between;
    padding: 15px;
    padding-bottom: 0;
    h1 {
      margin: 0;
      font-size: 22pt;
      font-weight: normal;
    }
    img {
      width: 26px;
    }
  }
  .modal-body {
    padding: 15px;
    
    video {
      width: 100%;
      max-width: 100%;
      height: auto;
      border-radius: 4px;
    }

    .scan-error-message {
      margin: 12px 0 0;
      color: #c00;
      font-size: 14px;
      line-height: 1.6;
      white-space: pre-wrap;
    }

    .scan-retry-button {
      margin-top: 8px;
      padding: 6px 12px;
      border: 1px solid #aaa;
      border-radius: 4px;
      background-color: #fff;
      color: #333;
      font-size: 14px;
    }
  }
}
</style>